import { FilterURI, type CompositeFilter } from '@sbt-web/network/types';
import { getLabelFromGeo } from '@sbt-web/utils';
import type { DataLayer, DLGeo } from '@/client/bridges/DataLayer';
import type { FiltersConfigState } from '@/reducers/filtersConfig';
import type { SearchState } from '@/reducers/search';
import transformAdType from '@/tools/tracking/transformer/AdTypeTransformer';
import transformPostalAddress from '@/tools/tracking/transformer/PostalAddressTransformer';
import { transformToLocations } from '@/tools/tracking/transformer/RadiusLocalityTransformer';
import { checkBooleanFilter } from './transformer/FilterTransformer';

export interface ParsedFilters {
  item: { uri: string; type?: string };
  values?: string[];
  locations?: [
    {
      '@type': 'PostalAddress';
      addressCountry: 'Italia';
      addressRegion?: string;
      addressLocality?: string;
      postalCode?: string;
      streetAddress?: string;
    },
  ];
  minValue?: string;
  maxValue?: string;
}

export function getAvailableFilters(
  searchState: SearchState,
  filtersConfigState: FiltersConfigState,
  searchData: DataLayer['search'],
  adsDataLength: number
): ParsedFilters[] {
  const parsedFilters: ParsedFilters[] = [
    {
      item: { uri: 'query' },
      values: [searchData.query ?? ''],
    },
    {
      item: { uri: 'num-results' },
      values: [adsDataLength?.toString()],
    },
    {
      item: { uri: 'ad-type' },
      values: [transformAdType(searchData.adType)],
    },
    {
      item: { uri: 'search-in-description' },
      values: checkBooleanFilter(searchData.filters, FilterURI.QuerySubjectOnly)
        ? ['true']
        : ['false'],
    },
    ...(checkBooleanFilter(searchData.filters, FilterURI.ItemShippable)
      ? [
          {
            item: { uri: 'delivery' },
            values: ['true'],
          },
        ]
      : []),
    ...(searchState?.geo?.zone?.value
      ? [
          {
            item: { uri: 'geo-zone' },
            values: [searchState?.geo?.zone?.value],
          },
        ]
      : []),
    {
      item: {
        uri: searchState?.radiusSearch != null ? 'radius' : 'geo',
        type: 'locations',
      },
      values:
        searchState?.radiusSearch != null
          ? [`${searchState?.radiusSearch.radiusMeters}`]
          : [getLabelFromGeo(searchState.geo, searchState.searchNearRegions)],

      locations:
        searchState?.radiusSearch != null
          ? [transformToLocations(searchState?.radiusSearch)]
          : [transformPostalAddress(searchData?.geo as DLGeo)],
    },
  ];

  const config = filtersConfigState.filtersStore;

  const availableFilters = config?.sparseFilters;

  if (availableFilters) {
    availableFilters.forEach((filter) => {
      if (searchData.filters[filter?.uri] !== undefined) {
        pushParsedFilters(parsedFilters, filter, searchData);
      } else {
        return;
      }
    });
  }

  return parsedFilters;
}

function pushParsedFilters(
  parsedFilters: ParsedFilters[],
  filter: CompositeFilter,
  searchData: DataLayer['search']
): void {
  if (filter.type === 'bool') {
    parsedFilters.push({
      item: filter,
      values: checkBooleanFilter(searchData?.filters, filter.uri)
        ? ['true']
        : [''],
    });
  } else if ('multiSelection' in filter) {
    parsedFilters.push({
      item: filter,
      values: searchData?.filters[filter?.uri]?.name?.split(', '),
    });
  } else {
    parsedFilters.push({
      item: filter,
      values: [searchData?.filters[filter?.uri]?.name],
    });
  }
}
